/**
 * .vpak container format — shared binary layout definitions.
 *
 * This module is the single source of truth for the on-disk/on-wire .vpak
 * layout. It is intentionally **pure**: no Node APIs, no DOM/Web APIs, no
 * Three.js. Both the offline cooker (`tools/cooker`) and the runtime
 * `BundleLoader` import it so the writer and reader can never drift.
 *
 * ── Layout ───────────────────────────────────────────────────────────────
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ Header        (HEADER_SIZE = 32 bytes, little-endian)             │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Blob region   (each blob 4-byte aligned; raw or brotli)           │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Index         (UTF-8 JSON, optionally brotli — see headerFlags)   │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * The index lives at the END of the file (after the blobs). The header carries
 * its absolute offset + byte length so a reader can fetch `[0, HEADER_SIZE)`
 * to learn where the index is, then range-fetch only the index, then
 * range-fetch individual blobs on demand. No central directory scan needed.
 *
 * ── Header (32 bytes) ────────────────────────────────────────────────────
 *
 *   off  size  field           notes
 *   0    4     magic           ASCII 'VPAK' (0x56 0x50 0x41 0x4B)
 *   4    4     version  u32     format version (FORMAT_VERSION)
 *   8    4     headerFlags u32  bit0: index is brotli-compressed
 *   12   4     reserved u32     must be 0
 *   16   8     indexOffset u64  absolute byte offset of the index region
 *   24   4     indexLength u32  byte length of the (possibly compressed) index
 *   28   4     headerCrc u32    CRC-32 of header bytes [0,28) (integrity guard)
 *
 * u64 is stored as two little-endian u32 words (low, then high) so the format
 * stays portable to environments without BigInt DataView helpers; in practice
 * `indexOffset` always fits in 32 bits but we reserve 64 for very large packs.
 *
 * ── Index JSON shape ─────────────────────────────────────────────────────
 *
 *   {
 *     "v": 1,                        // index schema version (== FORMAT_VERSION)
 *     "blobs": [                     // deduplicated blob table
 *       { "offset": 32, "length": 1234, "stored": 567, "sha256": "…", "flags": 1 }
 *     ],
 *     "entries": [                   // logical files → blob index
 *       { "path": "models/hero.glb", "blob": 0, "contentType": "model/gltf-binary" }
 *     ]
 *   }
 *
 * `length`  = original (uncompressed) byte length the reader must produce.
 * `stored`  = bytes actually written to the pack (== length when not compressed).
 * `flags`   bit0 (BLOB_FLAG_BROTLI): blob bytes are brotli-compressed.
 * `sha256`  = lowercase hex SHA-256 of the **original** (uncompressed) bytes.
 *
 * Multiple `entries` may reference the same `blob` index — that is how content
 * deduplication works (identical sha256 ⇒ one blob, many paths).
 */

// ── Constants ────────────────────────────────────────────────────────────

/** ASCII 'VPAK'. */
export const MAGIC = 0x4b415056; // little-endian u32 read of bytes 'V','P','A','K'
/** Magic as raw bytes, in file order. */
export const MAGIC_BYTES: readonly number[] = [0x56, 0x50, 0x41, 0x4b]; // 'V','P','A','K'
/** Current .vpak format version. */
export const FORMAT_VERSION = 1;
/** Size of the fixed header in bytes. Multiple of 4 for alignment. */
export const HEADER_SIZE = 32;
/** Blob offsets are aligned to this many bytes (typed-array friendliness). */
export const BLOB_ALIGN = 4;

/** Header flag bit: the index region is brotli-compressed. */
export const HEADER_FLAG_INDEX_BROTLI = 1 << 0;
/** Blob flag bit: the blob bytes are brotli-compressed. */
export const BLOB_FLAG_BROTLI = 1 << 0;

// ── Types ────────────────────────────────────────────────────────────────

/** One physical blob in the pack (after dedup). */
export interface VpakBlob {
  /** Absolute byte offset of the blob within the pack. Always BLOB_ALIGN-aligned. */
  offset: number;
  /** Original (uncompressed) length in bytes. */
  length: number;
  /** Stored length in bytes (== length unless compressed). */
  stored: number;
  /** Lowercase hex SHA-256 of the ORIGINAL (uncompressed) bytes. */
  sha256: string;
  /** Blob flags (BLOB_FLAG_*). */
  flags: number;
}

/** One logical file (path) in the pack. Points at a blob (many→one for dedup). */
export interface VpakEntry {
  /** POSIX-style relative path, e.g. "models/hero.glb". */
  path: string;
  /** Index into the blobs[] array. */
  blob: number;
  /** Optional MIME type (used by getBlobUrl + HTTP-y consumers). */
  contentType?: string;
}

/** The full parsed index. */
export interface VpakIndex {
  /** Index schema version (mirrors FORMAT_VERSION). */
  v: number;
  blobs: VpakBlob[];
  entries: VpakEntry[];
}

/** Parsed header fields. */
export interface VpakHeader {
  version: number;
  headerFlags: number;
  indexOffset: number;
  indexLength: number;
}

// ── Header encode / decode ───────────────────────────────────────────────

/**
 * Encode a 32-byte header. Computes and embeds the header CRC-32.
 */
export function encodeHeader(h: VpakHeader): Uint8Array {
  const buf = new Uint8Array(HEADER_SIZE);
  const dv = new DataView(buf.buffer);
  // magic
  buf[0] = MAGIC_BYTES[0];
  buf[1] = MAGIC_BYTES[1];
  buf[2] = MAGIC_BYTES[2];
  buf[3] = MAGIC_BYTES[3];
  dv.setUint32(4, h.version >>> 0, true);
  dv.setUint32(8, h.headerFlags >>> 0, true);
  dv.setUint32(12, 0, true); // reserved
  // indexOffset as u64 little-endian (low, high)
  const off = h.indexOffset;
  dv.setUint32(16, off >>> 0, true);
  dv.setUint32(20, Math.floor(off / 0x1_0000_0000) >>> 0, true);
  dv.setUint32(24, h.indexLength >>> 0, true);
  // header CRC over bytes [0,28)
  dv.setUint32(28, crc32(buf.subarray(0, 28)) >>> 0, true);
  return buf;
}

/**
 * Decode and validate a 32-byte header. Throws on bad magic, unsupported
 * version, or header CRC mismatch.
 */
export function decodeHeader(bytes: Uint8Array): VpakHeader {
  if (bytes.length < HEADER_SIZE) {
    throw new Error(`vpak: header too short (${bytes.length} < ${HEADER_SIZE})`);
  }
  if (
    bytes[0] !== MAGIC_BYTES[0] ||
    bytes[1] !== MAGIC_BYTES[1] ||
    bytes[2] !== MAGIC_BYTES[2] ||
    bytes[3] !== MAGIC_BYTES[3]
  ) {
    throw new Error('vpak: bad magic (not a .vpak file)');
  }
  // DataView must be created over the exact header window to respect byteOffset.
  const dv = new DataView(bytes.buffer, bytes.byteOffset, HEADER_SIZE);
  const version = dv.getUint32(4, true);
  if (version !== FORMAT_VERSION) {
    throw new Error(`vpak: unsupported version ${version} (expected ${FORMAT_VERSION})`);
  }
  const headerFlags = dv.getUint32(8, true);
  const offLo = dv.getUint32(16, true);
  const offHi = dv.getUint32(20, true);
  const indexOffset = offHi * 0x1_0000_0000 + offLo;
  const indexLength = dv.getUint32(24, true);
  const storedCrc = dv.getUint32(28, true);
  const calcCrc = crc32(bytes.subarray(0, 28)) >>> 0;
  if (storedCrc !== calcCrc) {
    throw new Error('vpak: header CRC mismatch (corrupt header)');
  }
  return { version, headerFlags, indexOffset, indexLength };
}

// ── Alignment helper ─────────────────────────────────────────────────────

/** Round `n` up to the next multiple of BLOB_ALIGN. */
export function alignUp(n: number, align: number = BLOB_ALIGN): number {
  const r = n % align;
  return r === 0 ? n : n + (align - r);
}

// ── CRC-32 (IEEE 802.3) — tiny, dependency-free integrity check ───────────
//
// Used only for the header self-check. Blob payloads use SHA-256 (stronger),
// but a full SHA-256 over the header would be overkill; CRC-32 catches the
// truncation / wrong-file / bit-rot cases we care about for the header.

let CRC_TABLE: Uint32Array | null = null;

function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    t[n] = c >>> 0;
  }
  CRC_TABLE = t;
  return t;
}

/** CRC-32 of a byte range. */
export function crc32(bytes: Uint8Array): number {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = t[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

// ── Content-type + compression heuristics (shared cooker/runtime defaults) ─

/**
 * Default MIME type for a path's extension. Returns undefined when unknown.
 * Kept deliberately small — covers the asset types the engine actually loads.
 */
export function contentTypeFor(path: string): string | undefined {
  const ext = extOf(path);
  return CONTENT_TYPES[ext];
}

/**
 * Whether a file SHOULD be brotli-compressed when cooking. Returns false for
 * formats that are already compressed (re-compressing wastes CPU and usually
 * grows the file). Returns true for text-ish / uncompressed formats.
 *
 * Unknown extensions default to `false` (safer: never inflate an opaque blob).
 */
export function shouldCompress(path: string): boolean {
  const ext = extOf(path);
  if (PRECOMPRESSED_EXT.has(ext)) return false;
  return COMPRESSIBLE_EXT.has(ext);
}

/** Lowercase extension including the dot, or '' if none. */
export function extOf(path: string): string {
  const clean = path.split('?')[0].split('#')[0];
  const slash = Math.max(clean.lastIndexOf('/'), clean.lastIndexOf('\\'));
  const base = slash === -1 ? clean : clean.slice(slash + 1);
  const dot = base.lastIndexOf('.');
  return dot === -1 ? '' : base.slice(dot).toLowerCase();
}

/** Extensions that are already compressed — never re-brotli these. */
export const PRECOMPRESSED_EXT: ReadonlySet<string> = new Set([
  '.ktx2', '.basis', '.glb', // glb usually carries Draco/Meshopt/KTX2 buffers
  '.jpg', '.jpeg', '.png', '.webp', '.avif', '.gif',
  '.mp3', '.ogg', '.oga', '.opus', '.m4a', '.aac', '.wav', // wav uncompressed but rarely shipped; treat as binary
  '.mp4', '.webm', '.mov',
  '.zip', '.gz', '.br', '.draco', '.bin', // .bin is opaque (often already-compressed gltf buffers)
  '.woff', '.woff2',
]);

/** Extensions worth brotli-compressing (text / markup / data). */
export const COMPRESSIBLE_EXT: ReadonlySet<string> = new Set([
  '.json', '.gltf', '.lua', '.txt', '.csv', '.svg', '.xml', '.html',
  '.css', '.js', '.mjs', '.glsl', '.wgsl', '.vert', '.frag', '.md', '.ndjson',
]);

const CONTENT_TYPES: Record<string, string> = {
  '.glb': 'model/gltf-binary',
  '.gltf': 'model/gltf+json',
  '.bin': 'application/octet-stream',
  '.ktx2': 'image/ktx2',
  '.basis': 'application/octet-stream',
  '.draco': 'application/octet-stream',
  '.json': 'application/json',
  '.ndjson': 'application/x-ndjson',
  '.lua': 'text/x-lua',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.svg': 'image/svg+xml',
  '.xml': 'application/xml',
  '.html': 'text/html',
  '.css': 'text/css',
  '.js': 'text/javascript',
  '.mjs': 'text/javascript',
  '.wgsl': 'text/plain',
  '.glsl': 'text/plain',
  '.vert': 'text/plain',
  '.frag': 'text/plain',
  '.md': 'text/markdown',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.avif': 'image/avif',
  '.gif': 'image/gif',
  '.mp3': 'audio/mpeg',
  '.ogg': 'audio/ogg',
  '.oga': 'audio/ogg',
  '.opus': 'audio/ogg',
  '.m4a': 'audio/mp4',
  '.aac': 'audio/aac',
  '.wav': 'audio/wav',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
};
